import React, { useState, ChangeEvent } from 'react';
import axios from '../api/myAxios';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Paper,
  Avatar,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Grid2,
  styled,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePageTitle } from '../hooks/usePageTitle';

// Types
interface UserProfileProps {
  _id: string;
  displayName: string;
  email: string;
  photoURL?: string;
  createdAt: string;
  lastLogin: string;
}

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const UserProfile: React.FC<UserProfileProps> = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [uploading, setUploading] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  usePageTitle('User Profile');

  // Fetch user profile with React Query
  const {
    data: profile,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['userProfile'],
    queryFn: () => {
      console.log('currentUser ----- ', currentUser?.metadata?.lastSignInTime);
      return axios
        .get('/api/users/profile')
        .then((res) => res.data.user as UserProfileProps);
    },
    enabled: !!currentUser,
  });

  // Mutation for photo upload
  const photoUploadMutation = useMutation({
    mutationFn: (formData: FormData) =>
      axios.post('/api/users/profile-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    },
    onError: (error) => {
      console.error(t('error.console.errorUploadingPhoto'), error);
      setError(t('profile.errorUploadPhoto'));
    },
  });

  const handlePhotoUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setError(null);
      const formData = new FormData();
      formData.append('photo', file);
      await photoUploadMutation.mutateAsync(formData);
    } catch (error) {
      console.error(t('error.console.errorUploadingPhoto'), error);
      setError(t('profile.errorUploadPhoto'));
    } finally {
      setUploading(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {fetchError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('profile.fetchingErrorMessage')}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Box position="relative">
            <Avatar
              src={profile?.photoURL}
              alt={profile?.displayName || t('profile.userProfile')}
              sx={{
                width: 120,
                height: 120,
                fontSize: '3rem',
                mb: 2,
                bgcolor: 'primary.main',
              }}
            >
              {profile?.displayName?.[0]?.toUpperCase() || '?'}
            </Avatar>
            <Box position="absolute" bottom={16} right={0}>
              <IconButton
                component="label"
                disabled={uploading || photoUploadMutation.isPending}
                sx={{
                  bgcolor: 'background.paper',
                  '&:hover': { bgcolor: 'background.default' },
                  boxShadow: 1,
                }}
              >
                <EditIcon color="primary" />
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                />
              </IconButton>
            </Box>
          </Box>

          <Typography variant="h4" component="h1" gutterBottom>
            {profile?.displayName || t('profile.userProfile')}
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {profile?.email}
          </Typography>

          <Grid2 container spacing={3} sx={{ mt: 3, mb: 2 }}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    whiteSpace: 'nowrap',
                    width: '100%',
                    fontSize: '0.75rem',
                  }}
                >
                  {t('profile.memberSince')}
                </Typography>
                <Typography variant="body1">
                  {profile?.createdAt
                    ? new Date(profile.createdAt).toLocaleDateString()
                    : '-'}
                </Typography>
              </Paper>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    whiteSpace: 'nowrap',
                    width: '100%',
                    fontSize: '0.75rem',
                  }}
                >
                  {t('profile.lastLogin')}
                </Typography>
                <Typography variant="body1">
                  {profile?.lastLogin
                    ? new Date(profile.lastLogin).toLocaleDateString()
                    : '-'}
                </Typography>
              </Paper>
            </Grid2>
          </Grid2>
        </Box>
      </Paper>
    </Container>
  );
};

export default UserProfile;
